import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { InviteRepository } from "@/repositories/invite.repository";
import { refusedByTheApi, type Invite } from "@shyne/shared/api";
import { toastError, toastSuccess } from "@/lib/utils";
import MainLayout from "@/components/layouts";

export default function VendorRelationshipInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [status, setStatus] = useState<"pending" | "accepted" | "declined" | "error">("pending");

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation token");
      setLoading(false);
      return;
    }

    const fetchInvite = async () => {
      try {
        const inviteData = await InviteRepository.readInvite(token);

        if (inviteData.kind !== "vendorRelationship") {
          setError("Invalid invitation type");
          setStatus("error");
          return;
        }

        setInvite(inviteData);
      } catch (err: unknown) {
        setError(refusedByTheApi(err) && err.status === 404 ? "This invitation was not found or has already been processed" : err instanceof Error ? err.message : "Failed to load invitation");
        setStatus("error");
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    if (!invite || !token) return;

    setProcessing(true);
    try {
      await InviteRepository.acceptVendorRelationshipInvite(token);

      setStatus("accepted");
      toastSuccess("Vendor relationship accepted successfully");

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err: any) {
      setError(err?.message || "Failed to accept invitation");
      setStatus("error");
      toastError(err?.message || "Failed to accept invitation");
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invite || !token) return;

    setProcessing(true);
    try {
      await InviteRepository.declineVendorRelationshipInvite(token);

      setStatus("declined");
      toastSuccess("Invitation declined");

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err: any) {
      setError(err?.message || "Failed to decline invitation");
      toastError(err?.message || "Failed to decline invitation");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex w-full justify-center py-20">
          <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (error && status === "error") {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate("/")}>Go to Home</Button>
        </div>
      </MainLayout>
    );
  }

  if (status === "accepted") {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
          <CheckCircle2 className="h-16 w-16 text-green-500" />
          <h2 className="text-2xl font-semibold">Invitation Accepted</h2>
          <p className="text-muted-foreground">You have successfully accepted the vendor relationship.</p>
        </div>
      </MainLayout>
    );
  }

  if (status === "declined") {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
          <XCircle className="h-16 w-16 text-red-500" />
          <h2 className="text-2xl font-semibold">Invitation Declined</h2>
          <p className="text-muted-foreground">You have declined the vendor relationship invitation.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Vendor Relationship Invitation</CardTitle>
            <CardDescription>
              {invite?.inviterName} wants to add your organization as a vendor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                By accepting, your organization will be linked as a vendor to {invite?.inviterName}.
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleDecline} variant="outline" className="flex-1" disabled={processing}>
                Decline
              </Button>
              <Button onClick={handleAccept} className="flex-1" disabled={processing}>
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Accept"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
